<template>
  <div class="relative h-full min-h-0 overflow-hidden bg-neutral-950">
    <!-- keep background image behind content -->
    <div class="absolute inset-0 z-0">
      <div class="hidden lg:block h-full w-full">
        <background-image :columnWidths="'623fr 157fr 312fr 32fr 436fr'" class="z-0" />
      </div>
      <div class="absolute inset-0 bg-neutral-950/70"></div>
    </div>

    <!-- Page -->
    <div class="relative z-10 grid h-full min-h-0 place-items-center px-4 py-10 sm:px-6 lg:px-16">
      <!-- Center card -->
      <div class="w-full max-w-md">
        <div class="overflow-hidden rounded-sm border border-slate-200 bg-white shadow-xl">
          <!-- Header -->
          <div class="border-b border-slate-100 px-6 py-5">
            <h1 class="scio-text-lead scio-weight-semibold text-slate-900">Secure Access</h1>
            <p class="mt-1 scio-text-body text-slate-600">
              Scio uses Single Sign-On only.
            </p>
          </div>

          <!-- Alerts -->
          <div class="px-6 pt-5">
            <div v-if="registrationSuccess"
              class="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 scio-text-body text-emerald-800">
              Registration successful! Please log in with your new account.
            </div>

            <div v-if="authError"
              class="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 scio-text-body text-red-700">
              {{ authError }}
            </div>

            <div v-else-if="identityProviderError"
              class="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 scio-text-body text-red-700">
              Single Sign-On is temporarily unavailable. Please contact the Scio team and include the time of the attempted sign-in.
            </div>

            <div v-if="sessionExpired"
              class="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 scio-text-body text-amber-800">
              Your session has expired. Please sign in again to continue.
            </div>
          </div>

          <!-- Body -->
          <div class="px-6 pb-6">
            <!-- PRIMARY: SSO -->
            <button class="mt-1 inline-flex h-11 w-full items-center justify-center gap-2 rounded-sm bg-scio-yellow-p3 px-4 scio-text-body scio-weight-semibold text-slate-900
                     shadow-sm transition hover:bg-scio-yellow-p2 focus:outline-none focus:ring-2 focus:ring-scio-yellow-p3/40 focus:ring-offset-2
                     disabled:cursor-not-allowed disabled:opacity-60" @click="handleMicrosoftLogin"
              :disabled="isLoading">
              <span v-if="isLoading"
                class="inline-block h-4 w-4 animate-spin rounded-full border-2 border-slate-900/30 border-t-slate-900"></span>
              <span>Continue with Jacobs SSO</span>
            </button>
          </div>

          <!-- Footer -->
          <div class="border-t border-slate-100 bg-slate-50 px-6 py-4">
            <p class="scio-text-meta text-center text-slate-500">
              Trouble signing in? Contact the Scio team at scio@jacobs.com.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BackgroundImage from '@/common/components/BackgroundImage.vue';
import authUserService from '@/services/auth-user.service';

export default {
  name: 'Login',
  components: { BackgroundImage },
  data() {
    return {
      registrationSuccess: false,
      identityProviderError: false,
      sessionExpired: false
    };
  },
  computed: {
    ...mapGetters('auth', ['authError', 'isAuthenticated', 'isLoading'])
  },
  methods: {
    ...mapActions('auth', ['clearAuthError']),

    async handleMicrosoftLogin() {
      // Primary path: always use interactive SSO.
      // If your auth service exposes beginSso(redirectUrl), use it.
      const target = this.$route.query?.redirect || "/programmes/";
      const redirectBack = `${window.location.origin}/#${target}`;

      if (typeof authUserService.beginSso === 'function') {
        authUserService.beginSso(redirectBack);
        return;
      }

      // Fallback to your existing endpoint pattern (kept simple, no token parsing)
      const useLocalApiProxy = String(import.meta.env.VITE_USE_LOCAL_API ?? '').toLowerCase() === 'true';
      const baseApi = useLocalApiProxy ? '' : (import.meta.env.VITE_SCIO_API_URL || '');
      const cacheBuster = Date.now();
      const interactiveUrl = `${baseApi}/api/AuthUser/sso-token?redirect=${encodeURIComponent(
        redirectBack
      )}&cb=${cacheBuster}`;

      window.location.href = interactiveUrl;
    }
  },
  async mounted() {
    // If already authenticated, skip login page
    if (this.isAuthenticated) {
      this.$router.push({ name: 'project-list' });
      return;
    }

    // Registration banner
    if (this.$route.params.registrationSuccess) {
      this.registrationSuccess = true;
    }

    this.identityProviderError = this.$route.query?.error === 'identity-provider';
    this.sessionExpired = this.$route.query?.reason === 'session-expired';

    this.clearAuthError();
  }
};
</script>
